import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cm_cart')) || []; } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('cm_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1, selectedCurrency = null, selectedVariant = null) => {
    // Use product's listed currency by default - buyer can override at checkout per order
    const currency = selectedCurrency || product.displayCurrency || 'BNB';
    setCart(prev => {
      const existing = prev.find(
        i => i.productId === product._id && i.selectedVariant === selectedVariant
      );
      if (existing) {
        return prev.map(i =>
          i.productId === product._id && i.selectedVariant === selectedVariant
            ? { ...i, quantity: Math.min(i.quantity + quantity, i.stock) }
            : i
        );
      }
      return [...prev, {
        productId: product._id,
        title: product.title,
        image: product.images?.[0]?.url || '',
        priceUSD: product.priceUSD,
        // The coin the seller listed in - used as DEFAULT payment for this item
        listedCurrency: product.displayCurrency || 'BNB',
        // Buyer-chosen override currency (starts as listed currency)
        selectedCurrency: currency,
        displayPriceWithFee: product.displayPriceWithFee,
        quantity,
        selectedVariant,
        stock: product.stock,
        // Seller info - needed to group into separate orders
        sellerId: product.seller?._id || product.seller,
        sellerUsername: product.seller?.username || '',
        shipsFrom: product.shipsFrom,
      }];
    });
  };

  const removeFromCart = (productId, selectedVariant = null) => {
    setCart(prev => prev.filter(
      i => !(i.productId === productId && i.selectedVariant === selectedVariant)
    ));
  };

  const updateQuantity = (productId, quantity, selectedVariant = null) => {
    if (quantity <= 0) { removeFromCart(productId, selectedVariant); return; }
    setCart(prev => prev.map(i =>
      i.productId === productId && i.selectedVariant === selectedVariant
        ? { ...i, quantity: Math.min(quantity, i.stock) }
        : i
    ));
  };

  // Buyer can change payment currency per item
  const updateItemCurrency = (productId, currency, selectedVariant = null) => {
    setCart(prev => prev.map(i =>
      i.productId === productId && i.selectedVariant === selectedVariant
        ? { ...i, selectedCurrency: currency }
        : i
    ));
  };

  const clearCart = () => setCart([]);

  // Group cart items by seller - each group = one separate order/payment
  const cartBySeller = cart.reduce((groups, item) => {
    const key = item.sellerId || 'unknown';
    if (!groups[key]) groups[key] = { sellerId: key, sellerUsername: item.sellerUsername, items: [] };
    groups[key].items.push(item);
    return groups;
  }, {});

  const sellerGroups = Object.values(cartBySeller);

  // Total item count
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  // Total in USD across all items (for display)
  const cartTotalUSD = cart.reduce((sum, i) => sum + (i.priceUSD * 1.015 * i.quantity), 0);

  // Number of separate payments needed
  const paymentCount = sellerGroups.length;

  return (
    <CartContext.Provider value={{
      cart,
      sellerGroups,
      paymentCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateItemCurrency,
      clearCart,
      cartCount,
      cartTotalUSD,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
